import { Component, useEffect, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { withFormik, Form } from "formik";

import { AreaRow, FormRow } from "../Form/Form";
import DialogComponentWrapper from "../Utils/DialogComponentWrapper";
import PromiseWrapper from "../Utils/PromiseDialog";

import { applyFormulas } from "../../utils/area";
import { DATA_TABLE, primeUpdate } from "../../utils/DataUtils";
import { useTreegridColsEffect } from "../../utils/tables";
import DataTablePrime from "../PrimeComponents/DataTablePrime";
import BulkEditBtnPrime from "../PrimeComponents/BulkEditBtnPrime";

const blockForm = "block_form";
const AREA_FORMAT_KEY = "number.dec.4";

class BlockForm extends Component {
    render() {
        const { status = {}, areaInfo, canEdit, data, t, handleSubmit, allowList, values } = this.props;
        if (!data) return null;
        const showAll = !Array.isArray(allowList) || !allowList.length;

        const isEdit = canEdit && status.edit;

        return (
            <div>
                <div>
                    <Form id={blockForm} onSubmit={handleSubmit}>
                        <div className='row'>
                            <div className='col-12 text-center'>
                                <FormRow
                                    edit={isEdit}
                                    dato1={data.nombre}
                                    nombre1={t("general.name")}
                                    campo1={(showAll || (allowList && allowList.includes("nombre"))) && "nombre"}
                                />

                                <AreaRow
                                    edit={isEdit}
                                    dato1={t("general.number_unit", { value: { number: data.superficie, unit: areaInfo.unidad } })}
                                    nombre1={t("area.area")}
                                    campo1={(showAll || (allowList && allowList.includes("superficie"))) && "superficie"}
                                    unit1={areaInfo.unidad}
                                />

                                {allowList && allowList.includes("superficie_dividida") && !showAll &&
                  <FormRow
                      edit={isEdit}
                      dato1={values.superficie_dividida}
                      nombre1={t("area.divided")}
                      campo1="superficie_dividida"
                      type1="checkbox"
                  />}

                                <FormRow
                                    edit={isEdit}
                                    dato1={t("general.number", { value: data.plantas })}
                                    nombre1={t("blocks.plantas")}
                                    campo1={(showAll || (allowList && allowList.includes("plantas"))) && "plantas"}
                                    type1="number"
                                    dato2={t("general.number", { value: data.goteros })}
                                    nombre2={t("blocks.goteros")}
                                    campo2={(showAll || (allowList && allowList.includes("goteros"))) && "goteros"}
                                    type2="number"
                                />

                                <FormRow
                                    edit={isEdit}
                                    dato1={data.year_plantacion}
                                    nombre1={t("blocks.anioPlantacion")}
                                    campo1={(showAll || (allowList && allowList.includes("year_plantacion"))) && "year_plantacion"}
                                    type1="number"
                                />

                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }
}

const FormikForm = withFormik({
    mapPropsToStatus: (props) => {
        return {
            edit: (props && props.edit) || false,
        };
    },
    mapPropsToValues: (props) => {
        const data = Object.assign({}, props.data);
        return {
            nombre: data.nombre,
            superficie: data.superficie,
            plantas: data.plantas,
            goteros: data.goteros,
            superficie_dividida: false,
            year_plantacion: data.year_plantacion
        };
    },
    enableReinitialize: true,
    handleSubmit: (values, { props }) => {
        props.setLoading(true);
        props.updateData({...values, bloque_id:props.data.bloque_id}, props.data, "bloque_id");
    }
})(withTranslation()(BlockForm));

const BlocksTable = ({ dataFarm, dispatch, canEdit, blocksInfo, sector_id, t }) => {
    const [dialogState, setDialogState] = useState({ visible: false });
    const [showDDB, setDropDown] = useState(false);
    const [tableBlocksInfo, setTableBlocksInfo] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const formikRef = useRef(false);
    const treeGrid = useRef(null);
    const promiseWrapper = useRef(null);
    const updatingRef = useRef(false);
    const awsOptions = useRef({
        promiseWrapper, t, dispatch, path: "blocks", canModify: ["nombre", "year_plantacion", "superficie", "kc", "plantas", "goteros"]
    });
    if (awsOptions.current.areaInfo !== dataFarm.areaInfo) awsOptions.current.areaInfo = dataFarm.areaInfo;
    if (awsOptions.current.dispatch !== dispatch) awsOptions.current.dispatch = dispatch;
    if (!awsOptions.current.extraData || awsOptions.current.extraData.sector_id !== sector_id) {
        awsOptions.current.extraData = { sector_id };
    }
    const campo_id = dataFarm && dataFarm.campo_id;
    const url = `/farms/${campo_id}/block`;
    if (awsOptions.current.url !== url) awsOptions.current.url = url;

    const selectItems = useMemo(() => [
        { id: "year_plantacion", text: t("blocks.anioPlantacion") },
        { id: "superficie,superficie_dividida", text: t("area.area") },
        { id: "plantas", text: t("blocks.plantas") },
        { id: "goteros", text: t("blocks.goteros") }
    ], [t]);

    useEffect(() => {
        const { formula_to_base: formula1, formula_from_kilo_base: formula2 } = dataFarm.areaInfo;
        blocksInfo.forEach(block => {
            if (!block) return;
            if (!block.apiArea && block.superficie) block.apiArea = block.superficie;
            if (block.apiArea) {
                // we can use formula2( formula1( sup / 10.000 ) )
                // instead of formula2( formula1( sup ) / 10.000 ) for area
                block.superficie = applyFormulas(block.apiArea / 10000, formula1, formula2, AREA_FORMAT_KEY);
            }
        });
        setTableBlocksInfo(blocksInfo);
    }, [dataFarm, blocksInfo]);

    const { names } = useMemo(() => {
        return {names: ["ID", t("general.name"), t("blocks.anioPlantacion"), t("area.area"), t("blocks.plantas"), t("blocks.goteros")]
        };
    }, [t, dataFarm]);

    const areaTemplate = (rowData, unidad) => {
        const { superficie: number } = rowData;
        return t("general.number_unit", { value: { number, unidad } }) || "-";
    };
    const plantTemplate = (rowData) => {
        const { plantas: number  } = rowData;
        return t("general.number_unit", { value: { number } }) || "-";
    };
    const dropTemplate = (rowData) => {
        const { goteros: number  } = rowData;
        return t("general.number_unit", { value: { number } }) || "-";
    };
    useTreegridColsEffect(awsOptions, treeGrid, names, t);
    const [selectedBlocks, setSelectedBlocks] = useState([]);

    if (!Array.isArray(blocksInfo)) return null;

    const heightTable = Array.isArray(blocksInfo) &&
    (blocksInfo.length < 10 ? `${blocksInfo.length * 100}px` : "500px");

    return (<div>
        <PromiseWrapper
            ref={promiseWrapper}
            yesText={t("general.answerYes")} noText={t("general.answerNo")}
            headerText={t("zone.recalcHeader")} contentText={t("zone.recalcBody")}
        />
        {canEdit ? <div>
            <DialogComponentWrapper formikRef={formikRef} containerId="block-table-form"
                t={t} dialogState={dialogState} setDialogState={setDialogState}
            >
                <FormikForm
                    setLoading={setLoadingTable}
                    innerRef={formikRef}
                    canEdit edit
                    allowList={dialogState.allowList}
                    farmId={campo_id}
                    data={dialogState.data}
                    areaInfo={{ unidad: dataFarm.areaInfo ? ` (${dataFarm.areaInfo.kilo_unidad})` : "" }}
                    updateData={(data, previousData, key) => primeUpdate(key, data, previousData,awsOptions, tableBlocksInfo, setTableBlocksInfo, DATA_TABLE, setDialogState, setLoadingTable, dialogState,null, selectedBlocks)}
                />
            </DialogComponentWrapper>
            <BulkEditBtnPrime divId="blocksTableToolbar" enabled={Boolean(showDDB)} items={selectItems} openDialog={setDialogState} headerText={t("forms.bulk")} />

        </div> : null}
        <DataTablePrime
            t={t}
            loading={loadingTable}
            items={tableBlocksInfo}
            scrollHeight={heightTable}
            editableRow
            selectionMode={canEdit?"checkbox":null}
            selection={selectedBlocks}
            onSelectionChange={(e) => {
                setSelectedBlocks(e.value);
                setDropDown(Boolean(e.value && e.value.length));
            }}
            editFunction = {(event, rowData) => setDialogState({ target:event.target, data: rowData, headerText: t("forms.edit"), visible: true })}
            rowDataBoundFunction= {(rowData) => {
                if (!rowData?.isNotEditable) return true;
                else return false;
            }}
            size="small"
            fields={[
                {field:"bloque_id",header:names[0], hidden:true, bodyStyle:{textAlign:"center"}},
                {field:"nombre",header:names[1], bodyStyle:{textAlign:"center"}},
                {field:"year_plantacion",header:names[2], bodyStyle:{textAlign:"center"}},
                {field:"superficie",body:((e) => areaTemplate(e,(dataFarm.areaInfo ? dataFarm.areaInfo.kilo_unidad : ""))), header:names[3], bodyStyle:{textAlign:"center"}},
                {field:"plantas",body:plantTemplate, header:names[4], bodyStyle:{textAlign:"center"}},
                {field:"goteros",body:dropTemplate,header:names[5], bodyStyle:{textAlign:"center"}},
            ]}
        />
    </div>);
};

export { BlocksTable };
